'use client';

import { forwardRef, useCallback } from 'react';
import { NumberInput as MantineNumberInput } from '@mantine/core';
import type { NumberInputProps as MantineNumberInputProps } from '@mantine/core';

import { DEFAULT_DEBOUNCE_DELAY, useDebouncedCallback } from '@highlight-cp/hooks';

import type { LockedProps } from '../types';

type BaseNumberInputProps = Omit<
  MantineNumberInputProps,
  LockedProps | 'inputWrapperOrder' | 'onChange' | 'radius' | 'size' | 'variant' | 'withAsterisk'
>;

/**
 * Props for {@link NumberInput}.
 *
 * **Custom props:** Standard Mantine NumberInput behavior except those listed below. Supports
 * `label`, `description`, `error`, `placeholder`, `disabled`, `required`, `min`, `max`, `step`,
 * `decimalScale`, `hideControls`, and other native / Mantine input-wrapper props where Mantine
 * forwards them (v7 replaced `precision`/`parser`/`formatter` with react-number-format props).
 *
 * **Debouncing:** Set `debounced` to debounce `onChange` (see {@link debounced}, {@link debounceMs}). With
 * a **controlled** `value`, the display follows parent state, which usually only updates after the debounced
 * callback—typing can feel laggy; prefer **uncontrolled** (omit `value`, optional `defaultValue`) when using
 * `debounced`, or avoid `debounced` if the parent must mirror every keystroke.
 *
 * **Omitted (not supported):** className, classNames, radius, shadow, size, style, styles, sx,
 * unstyled, variant, withAsterisk. Prefer form-level required handling over `withAsterisk`.
 */
export type NumberInputProps = BaseNumberInputProps & {
  /**
   * Change handler with the v6-era signature: numeric values arrive as `number`,
   * an empty input arrives as `''` (Mantine v7's raw `string | number` values are
   * normalized internally). Method syntax on purpose: params stay bivariant, so
   * pre-v7 handlers typed `(value: number) => void` keep compiling.
   */
  onChange?(value: number | ''): void;
  /**
   * When true, `onChange` is debounced by {@link debounceMs} to limit rapid updates (e.g. before
   * API calls).
   *
   * **Uncontrolled** (no `value`): Mantine keeps the displayed value in sync while typing; only the
   * callback to your code is delayed.
   *
   * **Controlled** (`value` from React state): The display follows `value`, which typically updates
   * only when `onChange` runs—so with debouncing, updates can lag behind typing. Prefer uncontrolled
   * when using this flag, or skip `debounced` if parent state must update on every keystroke.
   */
  debounced?: boolean;
  /**
   * Debounce delay in milliseconds when {@link debounced} is true.
   * @defaultValue {@link DEFAULT_DEBOUNCE_DELAY} from `@highlight-cp/hooks` (500).
   */
  debounceMs?: number;
};

/**
 * ### NumberInput
 *
 * @description
 * Wrapper around Mantine `NumberInput` with a restricted public API (no direct `classNames` /
 * `className` / sizing tokens on the control). Visual defaults come from the app theme
 * (`NumberInput` and shared `Input` styles in `@highlight-cp/theme`). Label, error, and
 * description order is fixed for consistent form layouts.
 *
 * @example
 * <NumberInput label="Quantity" min={1} value={n} onChange={setN} />
 *
 * @example
 * <NumberInput debounced label="Filter" onChange={onDebouncedChange} />
 *
 * @param props - {@link NumberInputProps}
 * @returns React element
 */
/** Normalizes Mantine v7's `string | number` change values to the v6 `number | ''` shape. */
const toLegacyValue = (value: string | number): number | '' => {
  if (typeof value === 'number') {
    return value;
  }
  const parsed = Number(value);
  return value.trim() === '' || Number.isNaN(parsed) ? '' : parsed;
};

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>((props, ref) => {
  const { debounced = false, debounceMs = DEFAULT_DEBOUNCE_DELAY, onChange, ...rest } = props;

  const invokeOnChange = useCallback(
    (value: string | number) => {
      onChange?.(toLegacyValue(value));
    },
    [onChange],
  );

  const debouncedOnChange = useDebouncedCallback(invokeOnChange, debounceMs);

  const handleChange = debounced ? debouncedOnChange : invokeOnChange;

  return (
    <MantineNumberInput
      ref={ref}
      {...rest}
      onChange={handleChange}
      inputWrapperOrder={['label', 'input', 'error', 'description']}
    />
  );
});

NumberInput.displayName = 'NumberInput';
